"use client";

/**
 * Maintenance-mode detector.
 *
 * Mirrors the backend's `MaintenanceModeMiddleware` (see
 * web-app/backend/app/middleware.py): when the operator flips
 * `MAINTENANCE_MODE=1` on the API and restarts, every `/api/*` request
 * except `/api/health` comes back 503. The frontend has no direct
 * signal for the flip - we just watch responses and trip a global
 * takeover when we see a 503 from an API path.
 *
 * Implementation notes:
 *
 * - We monkeypatch `window.fetch` once on client mount. That's blunt,
 *   but auth-context + a handful of one-off `fetch()` calls across the
 *   app don't go through our `fetchWithRetry` helper, and wrapping them
 *   all would touch 10+ files. Patching `fetch` is strictly additive:
 *   we pass through every response; we only peek at the status.
 *
 * - While in maintenance we poll a non-allowlisted endpoint every
 *   30 s. When it answers with anything other than 503 we drop the
 *   flag and the takeover hides, no page reload needed. We cannot
 *   probe `/api/health` for this: it's in the backend's allowlist and
 *   returns 200 even during maintenance, so it's useless as a
 *   recovery signal. `/api/auth/me` is the probe of choice - cheap,
 *   idempotent, 401 for anon / 200 for signed-in, and (crucially)
 *   503'd by the middleware when maintenance is on.
 *
 * - Detection only flags on 503s *from our API*, identified by path
 *   prefix. Third-party 503s (CF Pages, Ollama, whatever) don't trip
 *   the screen - important because the takeover is non-dismissible.
 *
 * - It also flags when our API cannot be reached at all and a direct
 *   /health check fails too while the device is online: the host is
 *   powered off (a rescale) or down, and the API cannot send its 503.
 *   Aborted fetches never count. The last announced end is kept in
 *   localStorage for three hours, so a reload during the power-off
 *   still shows the time (2026-10-22, the CAX31 rescale).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { API_BASE } from "./api";
import {
  MAINTENANCE_STORE_KEY,
  isAbortError,
  readMaintenanceBody,
  savedAnnouncement,
  type MaintenanceReason,
} from "./maintenance-eta";

interface MaintenanceContextValue {
  inMaintenance: boolean;
  /** Epoch ms of the last recovery probe (or the moment the 503 was
   *  detected). Drives the "Son yoxlama: HH:MM" line on the takeover
   *  so the user can see the screen is actually re-checking. */
  lastCheckedAt: number | null;
  /** The planned end from the 503 body (ISO 8601), when the operator set
   *  one. Kept while the server is off: the probe then fails as a network
   *  error (the edge's error page has no CORS header) and leaves it. */
  until: string | null;
  /** A known reason key from the 503 body ("server_upgrade"). */
  reason: MaintenanceReason | null;
}

const MaintenanceContext = createContext<MaintenanceContextValue>({
  inMaintenance: false,
  lastCheckedAt: null,
  until: null,
  reason: null,
});

export function useMaintenance() {
  return useContext(MaintenanceContext);
}

/** True when `url` is one of our API endpoints (whatever origin). */
function isApiUrl(url: string): boolean {
  // API_BASE is either absolute ("https://api.menzil.ai") or the
  // same-origin rewrite target "/api". In both cases we only care
  // about path-level prefix match.
  try {
    const u = new URL(url, window.location.origin);
    if (API_BASE.startsWith("http")) {
      const base = new URL(API_BASE);
      return u.origin === base.origin && u.pathname.startsWith(base.pathname);
    }
    return u.origin === window.location.origin && u.pathname.startsWith(API_BASE);
  } catch {
    return false;
  }
}

// No probe-URL filter. The recovery probe below hits the same /auth/me
// endpoint the detector watches - during maintenance the probe gets
// 503 and the detector's "already in maintenance" state stays true
// (idempotent); on recovery the probe gets 200/401, the detector
// doesn't trip, and the probe callback clears the flag. Using an
// allowlisted endpoint for the probe would look cleaner but can't
// distinguish "maintenance is over" from "health is always up".

export function MaintenanceProvider({ children }: { children: ReactNode }) {
  const [inMaintenance, setInMaintenance] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const [until, setUntil] = useState<string | null>(null);
  const [reason, setReason] = useState<MaintenanceReason | null>(null);
  // The 503 says when and why; a later 503 may move the end. The tab
  // also keeps it, so a reload while the host is off still shows it.
  const readBody = useCallback((res: Response) => {
    res.clone().json().then((body) => {
      const m = readMaintenanceBody(body);
      if (m.until) setUntil(m.until);
      if (m.reason) setReason(m.reason);
      if (m.until || m.reason) {
        try {
          localStorage.setItem(MAINTENANCE_STORE_KEY, JSON.stringify({ ...m, at: Date.now() }));
        } catch { /* storage blocked: the screen still shows, without a time after a reload */ }
      }
    }).catch(() => { /* a 503 without JSON: plain copy */ });
  }, []);

  // A fetch to the API failed without any answer. That alone proves
  // little (a dropped request, a closed laptop lid), so ask /health
  // directly, outside the wrapper: if that fails too while the device
  // is online, the API host is unreachable (powered off for a rescale,
  // or down) and the takeover shows. Cloudflare's error page for a dead
  // origin carries no CORS header, which is why it arrives here as a
  // network error rather than as a status.
  const checkingRef = useRef(false);
  const inMaintenanceRef = useRef(false);
  useEffect(() => { inMaintenanceRef.current = inMaintenance; }, [inMaintenance]);
  const confirmOutage = useCallback(async (direct: typeof fetch) => {
    // Read fresh each time: the device can go offline during the check.
    const online = () => navigator.onLine !== false;
    if (checkingRef.current || inMaintenanceRef.current || !online()) return;
    checkingRef.current = true;
    try {
      await direct(`${API_BASE}/health`, { cache: "no-store" });
      // Any answer means the host is up: an ordinary failed request.
    } catch {
      if (online()) {
        let saved: ReturnType<typeof savedAnnouncement> = null;
        try { saved = savedAnnouncement(localStorage.getItem(MAINTENANCE_STORE_KEY), Date.now()); } catch { /* no storage */ }
        if (saved?.until) setUntil(saved.until);
        if (saved?.reason) setReason(saved.reason);
        setInMaintenance(true);
        setLastCheckedAt(Date.now());
      }
    } finally {
      checkingRef.current = false;
    }
  }, []);

  // Track the last fetch we patched so hot-reloads don't stack wrappers.
  const patchedRef = useRef(false);

  // Wrap window.fetch once per page load (client only).
  useEffect(() => {
    if (typeof window === "undefined" || patchedRef.current) return;
    patchedRef.current = true;

    const originalFetch = window.fetch.bind(window);
    const urlOf = (input: RequestInfo | URL) =>
      typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url ?? "";
    window.fetch = async (input, init) => {
      let res: Response;
      try {
        res = await originalFetch(input, init);
      } catch (err) {
        if (!isAbortError(err) && isApiUrl(urlOf(input))) void confirmOutage(originalFetch);
        throw err;
      }
      // Only trip on API 503s. Health is excluded to avoid the
      // recovery probe feeding itself back into the detector.
      if (res.status === 503) {
        const url = urlOf(input);
        if (isApiUrl(url)) {
          setInMaintenance(true);
          setLastCheckedAt(Date.now());
          readBody(res);
        }
      }
      return res;
    };

    // No cleanup: restoring the original would run on every effect
    // re-run in dev. A single page load = a single wrapper; a reload
    // gets a fresh, unwrapped fetch from the new environment.
  }, [readBody, confirmOutage]);

  // Recovery probe - poll /api/auth/me while in maintenance, clear the
  // flag once it comes back as anything other than 503. /auth/me is
  // NOT in the backend's maintenance allowlist, so it genuinely flips
  // from 503 → 200/401 the moment maintenance ends. /api/health is
  // the wrong signal - it's always 200 so the flag would clear
  // immediately and the takeover would flash in/out.
  const probeRecovery = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        cache: "no-store",
        credentials: "include",
      });
      if (res.status !== 503) {
        setInMaintenance(false);
        setUntil(null);
        setReason(null);
        try { localStorage.removeItem(MAINTENANCE_STORE_KEY); } catch { /* no storage */ }
      }
      setLastCheckedAt(Date.now());
    } catch {
      /* network failure - keep screen up, try again on next tick */
      setLastCheckedAt(Date.now());
    }
  }, []);

  useEffect(() => {
    if (!inMaintenance) return;
    const id = setInterval(probeRecovery, 30_000);
    // Kick one off immediately so a very short maintenance window
    // doesn't leave the user staring at the takeover for 30 s after
    // the backend is already back.
    const t = setTimeout(probeRecovery, 5_000);
    return () => {
      clearInterval(id);
      clearTimeout(t);
    };
  }, [inMaintenance, probeRecovery]);

  const value = useMemo(() => ({ inMaintenance, lastCheckedAt, until, reason }), [inMaintenance, lastCheckedAt, until, reason]);
  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
}
